// src/app/route-error-element.tsx
// The router's `errorElement`: classify what React Router caught, report it, render the
// surface. Mounted on every route in `router.tsx` — on the leaf children as `variant="region"`
// so the nav rail survives a single page's failure, and on the `/` parent as `variant="page"`
// for a failure that took the shell with it.
//
// This file, not `error-page.tsx`, owns the three decisions that need real context:
//   1. WHICH KIND — a 404 response, a stale-deploy chunk failure, a lost connection, or a
//      genuine crash. Getting this wrong is what makes error copy feel machine-written.
//   2. WHETHER TO REPORT — only a genuine crash. A 404, an offline blip and a stale chunk are
//      expected states, and `observability/sentry.ts` already lists `/^Loading chunk \d+ failed/`
//      in `ignoreErrors` for exactly that reason. Reporting them would bury the real ones.
//   3. WHETHER A STACK IS VISIBLE — gated on `import.meta.env.DEV` HERE, so a deployed build
//      passes `details: undefined` and the surface has nothing to leak.
import { useMemo } from 'react';
import { isRouteErrorResponse, useNavigate, useRevalidator, useRouteError } from 'react-router-dom';
// Direct from the SDK rather than a helper on `observability/sentry.ts`: that module
// deliberately exports no capture helper, and adding one would widen its surface. Import is
// safe because every event — captureException included — passes through the `beforeSend` set
// in `buildInitOptions`, which REBUILDS the event from an allowlist (`buildSafeEvent`) and
// withholds the exception message entirely. With no `VITE_SENTRY_DSN` (and on localhost)
// `init` was never called, so this no-ops with no transport and simply returns an id.
//
// It also closes a real gap: a boundary that catches a render error stops it reaching
// `globalHandlersIntegration`, so before this file those crashes were invisible to Sentry.
import { captureException } from '@sentry/react';
import { formatErrorDetails, isChunkLoadError } from './error-classify';
import { AppErrorPage, type AppErrorKind } from './error-page';
import { useHomePath } from '@/shared/lib/home-path';

/**
 * Normalise the two shapes a "thrown response" arrives in.
 *
 * MEASURED 2026-09-08: `isRouteErrorResponse` is false for a bare `Response`. It tests for
 * React Router's internal `ErrorResponse` wrapper, which the router builds only when a
 * LOADER or action throws. A throw from a component's render is passed through unwrapped, so
 * keying on `isRouteErrorResponse` alone silently classified a 404 as a crash — and would
 * have reported it to Sentry as one.
 */
function asErrorResponse(
  err: unknown,
): { status: number; statusText: string; data?: unknown } | undefined {
  if (isRouteErrorResponse(err)) {
    return { status: err.status, statusText: err.statusText, data: err.data };
  }
  if (typeof Response !== 'undefined' && err instanceof Response) {
    return { status: err.status, statusText: err.statusText };
  }
  return undefined;
}

/** Classify a caught value into one of the four surfaces. Order matters — see each branch. */
export function classifyRouteError(err: unknown): AppErrorKind {
  // A thrown response wins outright: the server or loader already told us what this is.
  if (asErrorResponse(err)?.status === 404) return 'notFound';
  // Before "crash": a failed import while the tab is offline is a connection problem, and
  // telling the user to reload for a new version would be a lie they cannot act on.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
  if (isChunkLoadError(err)) return 'chunk';
  return 'crash';
}

/**
 * Router-aware detail text. A thrown response has no stack — its status line IS the detail.
 */
function formatDetails(err: unknown): string {
  const res = asErrorResponse(err);
  if (res) {
    const body = res.data ? `
${String(res.data)}` : '';
    return `${res.status} ${res.statusText}${body}`;
  }
  return formatErrorDetails(err);
}

/**
 * Report-once ledger, keyed by the error OBJECT.
 *
 * MEASURED 2026-09-08: a component-scoped `useMemo` guard was not enough — one crash produced
 * TWO Sentry events. React re-renders a failing subtree a second time to recover a better
 * component stack, which remounts the `errorElement` and so re-runs its memo. Keying on the
 * error itself survives any number of mounts (including a revalidation that fails again with
 * the same object), and a `WeakMap` lets the entry be collected with the error.
 */
const reported = new WeakMap<object, string>();

function reportOnce(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const seen = reported.get(error);
    if (seen !== undefined) return seen;
    console.error('[RouteErrorElement] crash', error);
    const id = captureException(error);
    reported.set(error, id);
    return id;
  }
  console.error('[RouteErrorElement] crash', error);
  return captureException(error);
}

export interface RouteErrorElementProps {
  /** `region` keeps the nav rail (leaf routes); `page` fills the viewport (shell routes). */
  variant?: 'page' | 'region';
}

export function RouteErrorElement({ variant = 'region' }: RouteErrorElementProps) {
  const error = useRouteError();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const homePath = useHomePath();
  const kind = classifyRouteError(error);

  // Reporting policy: only a genuine crash earns a Sentry event. A 404, an offline blip and
  // a stale chunk are expected states — `observability/sentry.ts` already lists
  // `/^Loading chunk \d+ failed/` in `ignoreErrors` for exactly that reason — and reporting
  // them would bury the real ones. Dedupe lives in `reportOnce`, not in this memo.
  const referenceId = useMemo(() => {
    if (kind !== 'crash') {
      // Still leave a local trace: a 404 or a stale chunk in the console is how this gets
      // triaged in dev, and neither carries PHI.
      console.warn(`[RouteErrorElement] ${kind}`, error);
      return undefined;
    }
    return reportOnce(error);
  }, [error, kind]);

  return (
    <AppErrorPage
      kind={kind}
      variant={variant}
      referenceId={referenceId}
      // THE production gate. A deployed build passes undefined, so no stack can render.
      details={import.meta.env.DEV ? formatDetails(error) : undefined}
      onPrimary={() => {
        if (kind === 'chunk') {
          // The old manifest is what's broken — only a real document load can fix it.
          window.location.reload();
          return;
        }
        if (kind === 'notFound') {
          navigate(homePath, { replace: true });
          return;
        }
        // React Router 7 resets the error boundary on revalidate, so a transient failure
        // recovers in-place with the rail and scroll position intact — no full reload.
        revalidator.revalidate();
      }}
      // Every kind carries the design's secondary "Go to Today" (error-*.html `secondaryLabel`), the 404 too: there
      // the DS primary reads "Back to Today" and the secondary repeats the way out, as the design draws it.
      secondaryLabel="Go to Today"
      onSecondary={() => navigate(homePath, { replace: true })}
    />
  );
}

/**
 * The `path: '*'` element — a REAL 404, replacing the silent `<Navigate to="/today">` that
 * used to sit at `router.tsx:68`. A dead or mistyped link landing the user on Today with no
 * explanation is indistinguishable from the app ignoring them (Ahmed's ruling, 2026-09-08).
 *
 * Not the same component as above because there is no thrown error to classify or report:
 * an unmatched route is a routing outcome, not a failure.
 */
export function NotFoundRoute() {
  const navigate = useNavigate();
  const homePath = useHomePath();
  return (
    <AppErrorPage
      kind="notFound"
      variant="region"
      onPrimary={() => navigate(homePath, { replace: true })}
      // The design's 404 (error-notfound.html) adds a secondary "Go to Today".
      secondaryLabel="Go to Today"
      onSecondary={() => navigate(homePath)}
    />
  );
}
